import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { toast } from "sonner";
import {
  CalendarDays, Clock, Users, Building2, Phone, Mail, User as UserIcon,
  ArrowRight, CheckCircle2, GraduationCap, Loader2, Sparkles, Video,
} from "lucide-react";

const SEMINAR_TITLE = "Free NYC Fire Alarm Career Seminar";
const SEMINAR_DESC = "Live 45-minute Q&A every Friday at 6 PM ET with instructor Trevor Marson. Walk parents and students through the S-98, S-95, S-12, S-13, F-53 exams, pay bands and how to land the first NYC fire alarm job with no prior experience. Free — no card required.";

export default function FreeSeminar() {
  useDocumentMeta({ title: `${SEMINAR_TITLE} — APM Fire Alarm Exam Prep`, description: SEMINAR_DESC, path: "/free-seminar" });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    role: "parent",
    role_title: "",
    num_students: "",
    slot_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/seminars/slots");
        setSlots(data.slots || []);
        if ((data.slots || []).length > 0) {
          setForm((f) => ({ ...f, slot_date: data.slots[0].date }));
        }
      } catch {
        toast.error("Couldn't load upcoming seminar dates. Please try again.");
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, []);

  const updateField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slot_date) {
      toast.error("Please pick an upcoming Friday.");
      return;
    }
    if (!form.full_name.trim() || !form.email.trim() || !form.phone.trim() || !form.organization.trim()) {
      toast.error("Name, email, phone, and organization are all required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/seminars/signup", {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        organization: form.organization.trim(),
        role: form.role,
        role_title: form.role_title.trim() || null,
        num_students: form.num_students.trim() || null,
        slot_date: form.slot_date,
        notes: form.notes.trim() || null,
      });
      setSuccess({ slot_label: data.slot_label, name: form.full_name.split(" ")[0] });
      toast.success("You're in! Confirmation email is on the way.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong — please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="free-seminar-page">
        {success ? (
          <SuccessPanel name={success.name} slotLabel={success.slot_label} />
        ) : (
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="overline mb-3"><span className="red-bar" />Free · Every Friday · 6 PM ET</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-4" data-testid="seminar-hero-title">
                Free Career <span className="text-red-600">Seminar</span> for NYC Fire Alarm.
              </h1>
              <p className="text-zinc-700 text-base leading-relaxed mb-5" data-testid="seminar-hero-lead">
                A 45-minute live walkthrough of the NYC fire alarm career path — the FDNY exams (S-98, S-95, S-12, S-13, F-53),
                the pay bands, and how to land your first job with no prior experience. Built for parents,
                students, and school partners looking to guide the next generation into a licensed trade.
              </p>
              <div className="space-y-3 mb-8">
                <FeatureRow icon={Video} title="Live Q&A with Trevor Marson" desc="15+ yrs NYC field experience · FDNY-certified instructor" />
                <FeatureRow icon={GraduationCap} title="Career map, exam-by-exam" desc="Which exam to take first, cost, prep time, expected income" />
                <FeatureRow icon={Building2} title="School partnerships welcome" desc="Bring your students, counselors, or after-school program" />
                <FeatureRow icon={Sparkles} title="No card, no upsell required" desc="Show up, ask questions, get answers. That's it." />
              </div>
              <div className="text-xs text-zinc-500 border-t border-zinc-200 pt-4">
                Prefer study material first?{" "}
                <Link to="/free-guide" className="text-red-600 hover:underline">Grab 25 free S-98 practice questions →</Link>
              </div>
            </div>

            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="card-tech p-6 sm:p-8" data-testid="seminar-form">
                <div className="mb-6">
                  <div className="overline mb-2"><span className="red-bar" />Pick your Friday</div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight mb-1">Upcoming seminars</h2>
                  <p className="text-sm text-zinc-500">All seminars run 6:00 – 6:45 PM ET on Zoom. Link emailed the day before.</p>
                </div>

                {loadingSlots ? (
                  <div className="text-sm text-zinc-500 italic py-6 text-center" data-testid="seminar-slots-loading">Loading available dates…</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-2 mb-6" data-testid="seminar-slots-grid">
                    {slots.map((s) => {
                      const active = form.slot_date === s.date;
                      return (
                        <button
                          type="button"
                          key={s.date}
                          onClick={() => setForm((f) => ({ ...f, slot_date: s.date }))}
                          className={`text-left border-2 px-3 py-2.5 transition-colors ${active ? "border-red-600 bg-red-50" : "border-zinc-200 hover:border-red-300"}`}
                          data-testid={`seminar-slot-${s.date}`}
                        >
                          <div className="flex items-center gap-2">
                            <CalendarDays className={`w-3.5 h-3.5 ${active ? "text-red-600" : "text-zinc-500"}`} />
                            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{active ? "Selected" : "Available"}</div>
                          </div>
                          <div className="font-semibold text-sm mt-1">{s.label}</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mb-6">
                  <div className="overline mb-2"><span className="red-bar" />I&apos;m signing up as a</div>
                  <div className="flex gap-2" data-testid="seminar-role-toggle">
                    {[
                      { id: "parent", label: "Parent / Guardian" },
                      { id: "student", label: "Student" },
                    ].map((r) => (
                      <button
                        type="button"
                        key={r.id}
                        onClick={() => setForm((f) => ({ ...f, role: r.id }))}
                        className={`flex-1 border-2 px-3 py-2 text-sm font-mono uppercase tracking-widest transition-colors ${form.role === r.id ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-red-300"}`}
                        data-testid={`seminar-role-${r.id}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <LabeledInput icon={UserIcon} label="Full name" value={form.full_name} onChange={updateField("full_name")} testid="seminar-input-name" required />
                  <LabeledInput icon={Mail} type="email" label="Email" value={form.email} onChange={updateField("email")} testid="seminar-input-email" required />
                  <LabeledInput icon={Phone} type="tel" label="Phone" value={form.phone} onChange={updateField("phone")} testid="seminar-input-phone" required />
                  <LabeledInput icon={Building2} label="School / organization" value={form.organization} onChange={updateField("organization")} testid="seminar-input-org" required placeholder={form.role === "parent" ? "Your school district or PTA" : "Your school or program"} />
                  <LabeledInput label="Your role / title (optional)" value={form.role_title} onChange={updateField("role_title")} testid="seminar-input-title" placeholder={form.role === "parent" ? "Parent, counselor, principal…" : "Grade / class"} />
                  <LabeledInput icon={Users} label="# of students (optional)" value={form.num_students} onChange={updateField("num_students")} testid="seminar-input-num" placeholder="e.g. bringing 12 seniors" />
                </div>

                <div className="mt-3">
                  <label className="block">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes / questions (optional)</div>
                    <textarea
                      value={form.notes}
                      onChange={updateField("notes")}
                      rows={3}
                      placeholder="Anything specific you want covered in the Q&A?"
                      className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-3 py-2 text-sm bg-white"
                      data-testid="seminar-input-notes"
                    />
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting || loadingSlots}
                  className="btn-primary w-full mt-6 justify-center"
                  data-testid="seminar-submit-btn"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Reserving your seat…</> : <>Reserve my free seat <ArrowRight className="w-4 h-4" /></>}
                </button>

                <p className="text-[11px] text-zinc-500 mt-3 text-center">
                  Zero card. Zero spam. One 45-minute Zoom, plus a confirmation email with a calendar invite.
                </p>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FeatureRow({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 bg-red-50 border border-red-100 grid place-items-center shrink-0">
        <Icon className="w-4 h-4 text-red-600" strokeWidth={2.25} />
      </div>
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-xs text-zinc-500 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function LabeledInput({ icon: Icon, label, value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}{required && <span className="text-red-600 ml-1">*</span>}</div>
      <div className="relative">
        {Icon && <Icon className="w-3.5 h-3.5 absolute top-3 left-3 text-zinc-400" />}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full border-2 border-zinc-200 focus:border-red-600 outline-none py-2 text-sm bg-white ${Icon ? "pl-9 pr-3" : "px-3"}`}
          data-testid={testid}
          required={required}
        />
      </div>
    </label>
  );
}

function SuccessPanel({ name, slotLabel }) {
  return (
    <div className="max-w-2xl mx-auto text-center py-12" data-testid="seminar-success">
      <div className="w-16 h-16 mx-auto bg-green-50 border border-green-200 grid place-items-center mb-5">
        <CheckCircle2 className="w-8 h-8 text-green-600" />
      </div>
      <div className="overline mb-2 justify-center"><span className="red-bar" />You&apos;re in</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3">
        See you Friday, {name}.
      </h1>
      <div className="bg-zinc-50 border-l-2 border-red-600 px-6 py-4 my-6 inline-block text-left">
        <div className="text-[10px] font-mono uppercase tracking-widest text-red-600">Your seminar slot</div>
        <div className="font-heading text-xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Clock className="w-5 h-5 text-red-600" /> {slotLabel}
        </div>
      </div>
      <p className="text-sm text-zinc-600 max-w-lg mx-auto leading-relaxed">
        We just emailed a confirmation. The Zoom link goes out the day before the seminar. Reply to that email if
        anything changes — we always keep a waitlist ready.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/free-guide" className="btn-outline text-sm">Grab 25 free S-98 questions →</Link>
        <Link to="/" className="btn-primary text-sm">Back to home</Link>
      </div>
    </div>
  );
}
